import PropTypes from 'prop-types';
import { trans } from '@spotahome/soyuz/client';
import { Mediator } from '@spotahome/soyuz-mediator';

import { Button, CHANNELS } from '@spotahome/ui-library';
import ListingChipsContainer from '@spotahome/ui-library/src/tenant/components/ListingChipsContainer';
import ListingTitle from '@spotahome/ui-library/src/tenant/components/ListingTitle';
import { formatFeaturesToChipList } from '@spotahome/ui-library/src/tenant/components/PropertyRulesRedesign/utils';

import { ListingSectionObservable } from '../ListingSection';

import openContractBasicsSimplifiedModal from './ContractBasicsSimplifiedModal';

import './ContractBasicsSimplified.scss';

const MAX_VISIBLE_CHIPS = 8;

const formatDocumentsToChipListSimplified = documentsPolicy => {
  if (!documentsPolicy?.requiredDocumentation?.documents?.length) return [];
  return documentsPolicy.requiredDocumentation.documents.map(documentKey => {
    const documentType = documentKey.replace('property.rule.document.', '');
    return {
      name: trans(
        `listing.simplification.bottom.contract-basics.document.${documentType}`
      ),
      value: true,
      icon: 'description'
    };
  });
};

const CONTRACT_TYPE_MODAL_CONTENT = {
  daily: {
    howItWorks: [
      'listing.simplification.bottom.contract-type.daily.how-it-works.1',
      'listing.simplification.bottom.contract-type.daily.how-it-works.2',
      'listing.simplification.bottom.contract-type.daily.how-it-works.3'
    ],
    checkout: [
      'listing.simplification.bottom.contract-type.daily.checkout.1',
      'listing.simplification.bottom.contract-type.daily.checkout.2'
    ],
    goodToKnow: [
      'listing.simplification.bottom.contract-type.daily.good-to-know.1',
      'listing.simplification.bottom.contract-type.daily.good-to-know.2'
    ]
  },
  fortnightly: {
    howItWorks: [
      'listing.simplification.bottom.contract-type.fortnightly.how-it-works.1',
      'listing.simplification.bottom.contract-type.fortnightly.how-it-works.2',
      'listing.simplification.bottom.contract-type.fortnightly.how-it-works.3'
    ],
    checkout: [
      'listing.simplification.bottom.contract-type.fortnightly.checkout.1',
      'listing.simplification.bottom.contract-type.fortnightly.checkout.2'
    ],
    goodToKnow: [
      'listing.simplification.bottom.contract-type.fortnightly.good-to-know.1',
      'listing.simplification.bottom.contract-type.fortnightly.good-to-know.2'
    ]
  },
  monthly: {
    howItWorks: [
      'listing.simplification.bottom.contract-type.monthly.how-it-works.1',
      'listing.simplification.bottom.contract-type.monthly.how-it-works.2'
    ],
    checkout: [
      'listing.simplification.bottom.contract-type.monthly.checkout.1',
      'listing.simplification.bottom.contract-type.monthly.checkout.2'
    ],
    goodToKnow: [
      'listing.simplification.bottom.contract-type.monthly.good-to-know.1',
      'listing.simplification.bottom.contract-type.monthly.good-to-know.2'
    ]
  }
};

const renderContractTypeSection = (titleKey, bulletKeys) => (
  <div className="contract-type-modal__section">
    <h3 className="contract-type-modal__section-title">{trans(titleKey)}</h3>
    <ul className="contract-type-modal__list">
      {bulletKeys.map(key => (
        <li key={key}>{trans(key)}</li>
      ))}
    </ul>
  </div>
);

const openContractTypeModal = contractType => () => {
  const content = CONTRACT_TYPE_MODAL_CONTENT[contractType];

  if (!content) {
    Mediator.publish(CHANNELS.OPEN_INFORMATION_MODAL, {
      title: trans('listing.pricing.details.type-of-contract'),
      body: (
        <>
          <p>
            <strong>{trans(`contract_type.label.${contractType}`)}</strong>
          </p>
          <p>{trans(`contract_type.descripition.${contractType}`)}</p>
        </>
      )
    });
    return;
  }

  Mediator.publish(CHANNELS.OPEN_INFORMATION_MODAL, {
    title: trans(
      `listing.simplification.bottom.contract-type.${contractType}.title`
    ),
    body: (
      <>
        <p className="contract-type-modal__intro">
          {trans(
            `listing.simplification.bottom.contract-type.${contractType}.intro`
          )}
        </p>
        {renderContractTypeSection(
          'listing.simplification.bottom.contract-type.how-it-works.title',
          content.howItWorks
        )}
        {renderContractTypeSection(
          'listing.simplification.bottom.contract-type.checkout.title',
          content.checkout
        )}
        {renderContractTypeSection(
          'listing.simplification.bottom.contract-type.good-to-know.title',
          content.goodToKnow
        )}
      </>
    )
  });
};

const buildContractTypeChip = contractType =>
  contractType
    ? {
        name: `${trans('listing.pricing.details.type-of-contract')}: ${trans(
          `contract_type.label.${contractType}`
        )}`,
        icon: 'receipt_long',
        value: true,
        onClick: openContractTypeModal(contractType)
      }
    : null;

const buildMinStayChip = minStay =>
  minStay
    ? {
        name: trans(
          'listing.simplification.bottom.contract-basics.min-stay.chip',
          { timeInDays: minStay }
        ),
        icon: 'date_range',
        value: true
      }
    : null;

const buildNoticePeriodChip = minimumNoticePeriod =>
  minimumNoticePeriod?.days
    ? {
        name: trans(
          'listing.simplification.bottom.contract-basics.notice-period.chip',
          { nDays: minimumNoticePeriod.days }
        ),
        icon: 'calendar_month',
        value: true,
        description: trans(
          'listing.simplification.bottom.contract-basics.notice-period.description',
          { nDays: minimumNoticePeriod.days }
        )
      }
    : null;

const CONTRACTUAL_PERMISSION_DESCRIPTIONS = {
  domiciliationAllowed:
    'listing.simplification.bottom.contract-basics.domiciliation.description',
  newContractValidForScholarship:
    'listing.simplification.bottom.contract-basics.scholarship.description'
};

const formatContractualPermissionsChips = (
  contractualPermissions,
  currency
) => {
  if (!contractualPermissions) return [];
  return Object.entries(contractualPermissions)
    .filter(([key, feature]) => key !== '__typename' && feature?.locoKey)
    .flatMap(([key, feature]) => {
      const chips = formatFeaturesToChipList(
        { [key]: feature },
        currency
      ).filter(Boolean);
      const descKey = CONTRACTUAL_PERMISSION_DESCRIPTIONS[key];
      return chips.map(chip =>
        descKey ? { ...chip, description: trans(descKey) } : chip
      );
    });
};

const ContractBasicsSimplified = ({
  contractualPermissions,
  contractType,
  documentsPolicy,
  minStay,
  minimumNoticePeriod = {},
  currency,
  bordered = true
}) => {
  const contractualPermissionsChips = formatContractualPermissionsChips(
    contractualPermissions,
    currency
  );

  const documentsPolicyChips =
    formatDocumentsToChipListSimplified(documentsPolicy);

  const chips = [
    ...contractualPermissionsChips,
    buildContractTypeChip(contractType),
    buildMinStayChip(minStay),
    buildNoticePeriodChip(minimumNoticePeriod)
  ].filter(Boolean);

  const documentChips = documentsPolicyChips;

  if (!chips.length) {
    return null;
  }

  return (
    <ListingSectionObservable
      section="contractBasics"
      className="contract-basics-simplified"
      bordered={bordered}
    >
      <ListingTitle role="tertiary">
        {trans('listing.simplification.bottom.contract-basics.card.title')}
      </ListingTitle>
      <ListingChipsContainer
        chipList={chips}
        maxVisibleChips={MAX_VISIBLE_CHIPS}
        showShowMoreButton={false}
        className="contract-basics-simplified__chips"
      />
      <Button
        type="button"
        color="link"
        className="contract-basics-simplified__view-more"
        onClick={() =>
          openContractBasicsSimplifiedModal({
            chips,
            documentChips,
            documentsAdditionalInfo:
              documentsPolicy?.requiredDocumentation?.additionalInfo,
            fullscreenOnMobile: true
          })
        }
      >
        {trans('common.view-more')}
      </Button>
    </ListingSectionObservable>
  );
};

ContractBasicsSimplified.propTypes = {
  contractualPermissions: PropTypes.shape({
    domiciliationAllowed: PropTypes.shape({
      icon: PropTypes.string,
      locoKey: PropTypes.string,
      additionalInfo: PropTypes.string
    }),
    newContractValidForScholarship: PropTypes.shape({
      icon: PropTypes.string,
      locoKey: PropTypes.string,
      additionalInfo: PropTypes.string
    })
  }).isRequired,
  contractType: PropTypes.string.isRequired,
  documentsPolicy: PropTypes.shape({
    requiredDocumentation: PropTypes.shape({
      documents: PropTypes.arrayOf(PropTypes.string),
      additionalInfo: PropTypes.string
    })
  }).isRequired,
  minStay: PropTypes.number.isRequired,
  minimumNoticePeriod: PropTypes.shape({
    days: PropTypes.number,
    price: PropTypes.number
  }),
  currency: PropTypes.string.isRequired,
  bordered: PropTypes.bool
};

export default ContractBasicsSimplified;
